import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Mail01Icon,
  WhatsappIcon,
  TelegramIcon,
  Copy01Icon,
  Location05FreeIcons,
  CopyLinkIcon,
  CancelCircleIcon,
} from "@hugeicons/core-free-icons";
import { ResponsiveModal } from "../ui/responsive-modal";
import { toast } from "sonner";
import Image from "next/image";
import { getAccommodationImageSrc } from "@/utils/getAccommodationImageSrc";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: string;
  thumbnail: string;
  title: string;
  url?: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  title = "اشتراک گذاری اقامتگاه",
  thumbnail,
  location,
  url,
}) => {
  const [copied, setCopied] = useState(false);

  // Get current page URL if not provided
  const shareUrl =
    url || (typeof window !== "undefined" ? window.location.href : "");

  const shareText = `${title}`;

  const handleEmailShare = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(`${title}\n\n${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`${shareText}\n${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleTelegramShare = () => {
    const text = encodeURIComponent(`${shareText}\n${shareUrl}`);
    window.open(
      `https://t.me/share/url?url=${shareUrl}&text=${text}`,
      "_blank"
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.custom((t) => (
        <div className="flex items-center gap-24 z-50 shadow-[0px_0px_8.1px_0px_#00000029] rounded-xl pl-2 pr-4 py-2 bg-white border-r-8 border-s-primary-500">
          <Typography variant="bodyMMedium">لینک کپی شد.‍</Typography>
          <Button variant={"ghost"} onClick={() => toast.dismiss(t)}>
            <HugeiconsIcon
              icon={CancelCircleIcon}
              className="size-6 text-primary-500"
            />
          </Button>
        </div>
      ));
      setTimeout(() => setCopied(false), 2000);
    } catch (_err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.custom((t) => (
          <div className="flex items-center gap-24 z-50 shadow-[0px_0px_8.1px_0px_#00000029] rounded-xl pl-2 pr-4 py-2 bg-white border-r-8 border-s-primary-500">
            <Typography variant="bodyMMedium">لینک کپی شد.‍</Typography>
            <Button variant={"ghost"} onClick={() => toast.dismiss(t)}>
              <HugeiconsIcon
                icon={CancelCircleIcon}
                className="size-6 text-primary-500"
              />
            </Button>
          </div>
        ));
        setTimeout(() => setCopied(false), 2000);
      } catch (_fallbackErr) {
        toast.error("خطا در کپی کردن لینک");
      }
      document.body.removeChild(textArea);
    }
  };

  const shareOptions = [
    {
      id: "copy",
      label: copied ? "کپی شد!" : "کپی کردن لینک",
      icon: CopyLinkIcon,
      onClick: handleCopyLink,
    },
    {
      id: "email",
      label: "ایمیل",
      icon: Mail01Icon,
      onClick: handleEmailShare,
    },
    {
      id: "whatsapp",
      label: "واتساپ",
      icon: WhatsappIcon,
      onClick: handleWhatsAppShare,
    },
    {
      id: "telegram",
      label: "تلگرام",
      icon: TelegramIcon,
      onClick: handleTelegramShare,
    },
  ];

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      className="md:max-w-lg"
      direction="bottom"
    >
      <div className="p-6 md:p-8 space-y-6">
        <Typography
          variant="bodySMedium"
          className="hidden md:block pt-8 text-lg"
        >
          اشتراک گذاری اقامتگاه
        </Typography>
        <div className="flex items-center gap-2">
          <Image
            width={114}
            height={74}
            src={getAccommodationImageSrc(thumbnail)}
            alt="اقامتگاه"
            className="w-28 h-[74px] rounded-lg object-cover"
          />
          <div className="flex-1 min-w-0">
            <Typography variant="bodySRegular" className="truncate">
              {title}
            </Typography>
            <Typography
              variant="captionRegular"
              className="flex items-center gap-1 pl-3"
              color="gray400"
            >
              <HugeiconsIcon icon={Location05FreeIcons} className="size-5" />
              {location}
            </Typography>
          </div>
        </div>

        {/* Share Options */}
        <div className="grid md:grid-cols-2 gap-4">
          {shareOptions.map((option) => (
            <Button
              key={option.id}
              variant="outline"
              onClick={option.onClick}
              className={`w-full justify-start gap-3 h-12 text-right rounded-lg border-gray-100 hover:border-primary-100 hover:text-primary-500`}
            >
              <HugeiconsIcon
                icon={option.icon}
                className="size-6 flex-shrink-0"
                strokeWidth={1.5}
              />
              <span className="flex-1 text-right">{option.label}</span>
            </Button>
          ))}
        </div>
      </div>
    </ResponsiveModal>
  );
};
